import { useState } from 'react'
import { NavLink } from 'react-router-dom'
import { LayoutDashboard, Upload, Clock, Table2, Activity, LogOut, X } from 'lucide-react'
import { useJobs } from '../hooks/useJobs'
import { useAuth } from '../context/AuthContext'

const NAV = [
  { to: '/',          label: 'Dashboard', icon: LayoutDashboard },
  { to: '/upload',    label: 'Upload',    icon: Upload           },
  { to: '/queue',     label: 'Queue',     icon: Clock            },
  { to: '/vat-chart', label: 'VAT Chart', icon: Table2           },
]

function LogoutModal({ email, onConfirm, onCancel }: {
  email: string | null
  onConfirm: () => void
  onCancel: () => void
}) {
  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(10,22,40,.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9999, padding: 20,
    }} onClick={onCancel}>
      <div style={{
        background: '#fff', borderRadius: 16, width: '100%', maxWidth: 380,
        boxShadow: '0 24px 60px rgba(0,0,0,.25)',
        overflow: 'hidden',
      }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{
          padding: '20px 24px 16px',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          borderBottom: '1px solid #f1f5f9',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10, flexShrink: 0,
              background: '#f1f5f9',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <LogOut size={18} color="#475569" />
            </div>
            <div>
              <p style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', margin: 0 }}>
                Sign out
              </p>
              <p style={{ fontSize: 12, color: '#94a3b8', margin: '2px 0 0' }}>
                {email}
              </p>
            </div>
          </div>
          <button onClick={onCancel} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#94a3b8', padding: 4, borderRadius: 6,
          }}>
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '18px 24px 22px' }}>
          <p style={{ fontSize: 14, color: '#475569', margin: '0 0 20px', lineHeight: 1.6 }}>
            You'll need to verify your email again to sign back in. Any unsaved work will remain in the queue.
          </p>

          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onCancel} style={{
              flex: 1, padding: '10px 0', borderRadius: 9,
              background: '#f1f5f9', color: '#475569',
              border: '1px solid #e2e8f0', cursor: 'pointer',
              fontFamily: 'inherit', fontSize: 13.5, fontWeight: 600,
            }}>
              Cancel
            </button>
            <button onClick={onConfirm} style={{
              flex: 1, padding: '10px 0', borderRadius: 9,
              background: '#0f1b35', color: '#fff',
              border: 'none', cursor: 'pointer',
              fontFamily: 'inherit', fontSize: 13.5, fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
            }}>
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function Layout({ children }: { children: React.ReactNode }) {
  const { jobs, stats } = useJobs()
  const { email, logout } = useAuth()
  const [showLogout, setShowLogout] = useState(false)

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>

      {/* Logout confirm modal */}
      {showLogout && (
        <LogoutModal
          email={email}
          onConfirm={() => { setShowLogout(false); logout() }}
          onCancel={() => setShowLogout(false)}
        />
      )}

      {/* Top header */}
      <header className="site-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img src="/logo.png" alt="finkraft.ai" style={{ height: 24, filter: 'brightness(1.1)' }} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 'auto' }}>

          {/* Active jobs badge */}
          {stats.files_active > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'rgba(0,180,216,.15)', border: '1px solid rgba(0,180,216,.3)',
              borderRadius: 99, padding: '4px 12px',
            }}>
              <Activity size={11} color="#00b4d8" />
              <span style={{ fontSize: 12, color: '#00b4d8', fontWeight: 600 }}>
                {stats.files_active} processing
              </span>
            </div>
          )}

          {/* User pill */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'rgba(255,255,255,.07)', border: '1px solid rgba(255,255,255,.12)',
            borderRadius: 99, padding: '4px 12px 4px 8px',
          }}>
            <div style={{
              width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
              background: '#0096c7',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, fontWeight: 700, color: '#fff',
            }}>
              {email?.charAt(0).toUpperCase()}
            </div>
            <span style={{ color: 'rgba(255,255,255,.75)', fontSize: 12.5, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {email}
            </span>
          </div>

          {/* Sign out button — text label, not just icon */}
          <button
            onClick={() => setShowLogout(true)}
            title="Sign out"
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'rgba(255,255,255,.07)', border: '1px solid rgba(255,255,255,.12)',
              borderRadius: 8, padding: '7px 12px', cursor: 'pointer',
              color: 'rgba(255,255,255,.65)', fontFamily: 'inherit',
              fontSize: 12.5, fontWeight: 500,
              transition: 'all .15s',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,.14)'
              ;(e.currentTarget as HTMLElement).style.color = '#fff'
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,.07)'
              ;(e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,.65)'
            }}
          >
            <LogOut size={13} />
            Sign out
          </button>
        </div>
      </header>

      {/* Sub nav */}
      <nav className="site-nav">
        {NAV.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
          >
            <Icon size={14} />
            {label}
          </NavLink>
        ))}
      </nav>

      {/* Page content */}
      <main className="page-main">
        {children}
      </main>
    </div>
  )
}